//nav bar imports
import React from 'react';
import "../styles/NavBar.css";
import logo from "../assets/logo.png";
import phoneIcon from "../assets/icons/phone.png"
import {Link, useNavigate, useLocation} from 'react-router-dom';
import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ArrowLeft } from 'lucide-react';



//Nav Bar
function NavBar({
    activeMenu, 
    setActiveMenu, 
    setDropdownOffset, 
    onHeightChange,  //from App
    contentRef,
    lenisRef
    }) {


const [isShrunk, setIsShrunk] = useState(false);
const [mobileOpen, setMobileOpen] = useState(false);
const [mobileMenuKey, setMobileMenuKey] = useState(null);
const mobileRef = useRef(null);
const burgerRef   = useRef(null);
const savedScrollRef = useRef(0);
const savedPathRef = useRef('');
const currentPathRef = useRef('');
const navigate = useNavigate();
const location = useLocation();
const prevMobileOpen = useRef(false);
const hasFadedIn = useRef(false);


const closeDrawer = () => {
  setMobileOpen(false);
  setMobileMenuKey(null);
};

const MOBILE_ITEMS = {
    home: [
        {label: 'Home',     to: '/'}
    ],
  products: [
    { label: 'Transformers',    to: '/sell-transformers' },
    { label: 'Pipes • Valves • Fittings', to: '/sell-pvf' },
    { label: 'Electrical',      to: '/sell-electrical'  },
    { label: 'Other Industrial Equipment',      to: '/sell-surplus'  },
  ],
  resources: [
    { label: 'Transformer Guide',  to: '/transformer-guides' },
    { label: 'PVF Guide',          to: '/pvf-guides'         },
    { label: 'Electrical Guide',   to: '/electrical-guides'  },
    { label: 'kVA Calculator',     to: '/kva-calculator'     },
    { label: 'Fault Current Calc', to: '/fault-current-calculator' },
    { label: 'PVF Sizing Calc',    to: '/pvf-sizing'         },
    { label: 'Payment Terms and Conditions',    to: '/payment-terms-and-conditions'         },
  ],
  who: [
    { label: 'About Us',       to: '/about-us' },
    { label: 'Company History',       to: '/company-history' },
    { label: 'Contact Us',       to: '/contact-us' },
        { label: 'Safety & Compliance',    to: '/safety-and-compliance'         },
    { label: 'FAQ',    to: '/faq'         }
  ]
};


useLayoutEffect(() => {
  if (mobileRef.current) {
    gsap.set(mobileRef.current, { yPercent: -100 });
  }
}, []);

useEffect(() => {
  function setVH() {
    let height = window.innerHeight;
    if ('visualViewport' in window && window.visualViewport) {
      height = window.visualViewport.height;
    }
    const vh = height * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  setVH();
  window.addEventListener('resize', setVH);
  window.addEventListener('orientationchange', setVH);

  if ('visualViewport' in window && window.visualViewport) {
    window.visualViewport.addEventListener('resize', setVH);
    window.visualViewport.addEventListener('scroll', setVH); // Handles address bar show/hide
  }

  return () => {
    window.removeEventListener('resize', setVH);
    window.removeEventListener('orientationchange', setVH);
    if ('visualViewport' in window && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', setVH);
      window.visualViewport.removeEventListener('scroll', setVH);
    }
  };
}, []);

useEffect(() => {
    function handleScroll() {
      // If scrolled more than 1px, shrink
      if (window.scrollY > 1) {
        setIsShrunk(true);
        onHeightChange(60);
      } else {
        setIsShrunk(false);
        onHeightChange(100);
      }
    }

    // Attach the scroll listener
    window.addEventListener('scroll', handleScroll);
    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


function handleMouseEnter(e, menuKey) {
    const rect= e.currentTarget.getBoundingClientRect();
    //setDropdownOffset({ top: rect.top + rect.height, left: rect.left });
    setDropdownOffset(rect.left);
    setActiveMenu(menuKey);
}

function handleMouseLeave() {
    setActiveMenu(null);
}

useEffect(() => {
  currentPathRef.current = location.pathname;
}, [location.pathname]);


/* ------------------------------------------------------------------
   GSAP – open/close drawer animation with scroll handling
-------------------------------------------------------------------*/
useEffect(() => {
  const panel = mobileRef.current;
  const content = contentRef?.current;

  if (!panel || !content || !window.matchMedia('(max-width:1050px)').matches) return;

  if (mobileOpen !== prevMobileOpen.current) {
    if (mobileOpen) {
      // Save scroll position and path only on initial open
      savedScrollRef.current = window.scrollY;
      savedPathRef.current = location.pathname;

      // Create timeline for opening
      const tl = gsap.timeline();

      // Fade out content simultaneously with menu slide-in
      tl.to(content, { opacity: 0, duration: 0.3, ease: 'power2.out' }, 0);

      // Slide in menu
      tl.to(panel, { yPercent: 0, duration: 0.0, ease: 'power2.in' }, 0);

      // At end of fade-out (0.3s), scroll to top and lock content
      tl.add(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        content.style.position = 'fixed';
        content.style.top = '0px';
        content.style.left = '0';
        content.style.width = '100%';
        document.documentElement.style.overflow = 'hidden';
        lenisRef.current?.stop();
      }, 0.3);

      // Then fade content back in
      tl.to(content, { opacity: 1, duration: 0.3, ease: 'power2.in' }, 0.3);
    } else {
      // Create timeline for closing
      const tl = gsap.timeline();

      // Fade out content
      tl.to(content, { opacity: 0, duration: 0.3, ease: 'power2.out' }, 0);

      // Slide out menu
      tl.to(panel, { yPercent: -100, duration: 0.35, ease: 'power2.in' }, 0);

      // At end of fade-out, unlock content and restore scroll conditionally
      tl.add(() => {
        content.style.position = '';
        content.style.top = '';
        content.style.left = '';
        content.style.width = '';
        document.documentElement.style.overflow = '';

        if (currentPathRef.current === savedPathRef.current) {
          window.scrollTo({ top: savedScrollRef.current, behavior: 'auto' });
        } else {
          window.scrollTo({ top: 0, behavior: 'auto' });
        }
        lenisRef.current?.start();

      }, 0.3);

      // Then fade content back in
      tl.to(content, { opacity: 1, duration: 0.3, ease: 'power2.in' }, 0.3);
    }
  }

  prevMobileOpen.current = mobileOpen;

  // Cleanup: reset styles on unmount or effect cleanup
  return () => {
    if (content) {
      content.style.position = '';
      content.style.top = '';
      content.style.left = '';
      content.style.width = '';
    }
    document.documentElement.style.overflow = '';
    lenisRef.current?.start();
  };
}, [mobileOpen, contentRef]);

/* ------------------------------------------------------------------
   GSAP – internal menu animations (main list / sub-menu)
-------------------------------------------------------------------*/
useEffect(() => {
  const panel = mobileRef.current;

  if (!mobileOpen || !panel || !window.matchMedia('(max-width:1050px)').matches) return;

  if (!mobileMenuKey) {
    const items = panel.querySelectorAll('[data-mobile-item]');
    const cta = panel.querySelector('[data-mobile-cta]');
    gsap.set([...items, cta], { autoAlpha: 1, y: 0 });
    return;
  }

  const sub = panel.querySelector('.mobile-sub');
  if (sub) {
    gsap.fromTo(
      sub,
      { xPercent: 100 },
      { xPercent: 0, duration: 0.5, ease: 'power2.in' }
    );
  }
}, [mobileOpen, mobileMenuKey]);

            

   /* ------------------------------------------------------------------
     Close the panel if user clicks outside it or presses Esc
  -------------------------------------------------------------------*/
  useEffect(() => {
    if (!mobileOpen) return;

    function handleClick(e) {

        if (burgerRef.current && burgerRef.current.contains(e.target)) return;

        if (mobileRef.current && !mobileRef.current.contains(e.target)) {
        closeDrawer();
      }
    }

    function handleEsc(e) {
      if (e.key === "Escape") handleClick(e);
    }

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keyup", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keyup", handleEsc);
    };
  }, [mobileOpen]);


        /* ────────────────────────────────────────────────────────
        AUTO‑CLOSE drawer when window exceeds 1050 px
        ──────────────────────────────────────────────────────── */
        useEffect(() => {
        function handleResize() {
            if (window.innerWidth > 1050) {
            // 1) force the panel off‑canvas
            gsap.set(mobileRef.current, { yPercent: -100 });
            // 2) reset React state so future clicks work as expected
            setMobileOpen(false);
            setMobileMenuKey(null);
            }
        }

        handleResize();                    // run once on mount
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
        }, []);


useLayoutEffect(() => {
  if (!contentRef?.current || !window.matchMedia('(max-width:1050px)').matches) return;
  if (!hasFadedIn.current) {
    gsap.set(contentRef.current, { opacity: 0 });
  }
}, [contentRef]);

useEffect(() => {
  if (!contentRef?.current || !window.matchMedia('(max-width:1050px)').matches) return;
  if (!hasFadedIn.current) {
    gsap.to(contentRef.current, { opacity: 1, duration: 0.3, ease: 'power2.in' });
    hasFadedIn.current = true;
  }
}, [contentRef]);


    return (

        <div
      id="nav-bar"
      // Condition for shrink class if scrolled down
      className={isShrunk ? 'shrink' : ''}
        >
            <div id="nav-bar-left-section">
                <Link to="/" className="logo-link" onClick={() => window.scrollTo(0, 0)}>
                    <div id="nav-bar-logo">
                        <img src={logo} alt="logo" />
                        <span id="logo-span">S&D Industrial Surplus</span>
                    </div>
                </Link>

                <div id="nav-bar-links">

                    
                    <div className={`nav-item-wrapper ${activeMenu === "products" ? "is-open" : ""}`}
                    onMouseEnter={(e) => handleMouseEnter(e, "products")}
                    onMouseLeave={handleMouseLeave}
                    >
                        <span className="nav-link">
                            Products & Services <NavArrow />
                        </span>
                    </div>

                    
                        <div className={`nav-item-wrapper ${activeMenu === "resources" ? "is-open" : ""}`}
                            onMouseEnter={(e) => handleMouseEnter(e, "resources")}
                            onMouseLeave={handleMouseLeave}
                        >
                            <span className="nav-link">
                                Resources <NavArrow />
                            </span>
                        </div>

                      
                    <div className={`nav-item-wrapper ${activeMenu === "who" ? "is-open" : ""}`}
                         onMouseEnter={(e) => handleMouseEnter(e, "who")}
                         onMouseLeave={handleMouseLeave}
                    >
                        <span className="nav-link">
                            Who We Are <NavArrow />
                        </span>
                    </div>                
                </div>
            </div>

            {/* ───────── Hamburger Icon ───────── */}
            <div className="mobile-icon-wrapper">
            {mobileOpen && mobileMenuKey && (
                <button className="back-btn" onClick={() => setMobileMenuKey(null)}>
                <ArrowLeft className="back-icon" />
                </button>
            )}
            <div
                ref={burgerRef}
                className={`menu-icon ${mobileOpen ? 'is-active' : ''}`}
                onClick={e => {
                e.stopPropagation();
                setMobileOpen(p => !p);
                }}
            >
            <span className="menu-icon__line menu-icon__line-left"></span>
            <span className="menu-icon__line"></span>
            <span className="menu-icon__line menu-icon__line-right"></span>
                </div>
            </div>
            

            {/* ── Mobile panel ───────────────────────── */}
            <div 
                ref={mobileRef}  
                className={`mobile-menu ${mobileOpen ? "open" : ""}`}
            >
            
           {/* ───────────────────────────────────────────
                SLIDING ZONE  (main list + sub‑menu)
            ─────────────────────────────────────────── */}
            <div className="mobile-content">

            {/* STEP 1 – top‑level menu  */}
            <ul className="mobile-top-list">
                {['home', 'products', 'resources', 'who'].map((key) => (
                <li key={key}>
                    <button
                    data-mobile-item
                    className="mobile-top"
                    onClick={() => {
                        if (key === 'home') {
                        closeDrawer();
                        navigate('/');
                        } else {
                        setMobileMenuKey(key);   // opens sub‑menu
                        }
                    }}
                    >
                    {key === 'home'
                        ? 'Home'
                        : key === 'products'
                        ? 'Products & Services'
                        : key === 'resources'
                        ? 'Resources'
                        : 'Who We Are'}
                    </button>
                </li>
                ))}
            </ul>

            {/* STEP 2 – sub‑menu (slides over main list) */}
            {mobileMenuKey && (
                <div className="mobile-sub">
                {/* header row: arrow + title */}
                <div className="mobile-sub-header">
                    <button
                    className="sub-back-btn"
                    aria-label="Go back"
                    onClick={() => {
                        const sub = mobileRef.current?.querySelector('.mobile-sub');
                        if (sub) {
                        gsap.to(sub, {
                            xPercent: 100,          // slide out to right
                            duration: 0.5,
                            ease: 'expo.in',
                            onComplete: () => setMobileMenuKey(null), // show main list
                        });
                        } else {
                        setMobileMenuKey(null);
                        }
                    }}
                    >
                    <ArrowLeft size={24} />
                    </button>
                    <span className="sub-title">
                    {mobileMenuKey.charAt(0).toUpperCase() + mobileMenuKey.slice(1)}
                    </span>
                </div>

                {/* sub‑links */}
                <div className="submenu-list">
                    {MOBILE_ITEMS[mobileMenuKey].map(({ label, to }) => (
                    <Link
                        key={label}
                        to={to}
                        data-mobile-item
                        onClick={() => {
                        closeDrawer();
                        navigate(to);
                        }}
                    >
                        {label}
                    </Link>
                    ))}
                </div>
                </div>
            )}
            </div>

            {/* ───────────────────────────────────────────
                PERSISTENT FOOTER  (phone + CTA)
            ─────────────────────────────────────────── */}
            
                <div className="mobile-cta-wrapper" data-mobile-cta>
                <a href="tel:8328852369" className="phone-link">
                <div className="phone-wrapper">
                    <img src={phoneIcon} alt="" className="phone-icon" />
                    <p>(832) 885-2369</p>
                </div>
                </a>

                <Link
                    to="/sell-surplus"
                    className="cta-link"
                    onClick={() => {
                    closeDrawer();      // hide drawer
                    }}
                >
                    SELL YOUR SURPLUS
                </Link>
                </div>

            </div>
            
            {mobileOpen && (
            <div
                className="mobile-backdrop"
                onClick={closeDrawer}
            />
            )}
            

            <div id="nav-bar-right-section">
                <a href="tel:8328852369" className="phone-link">
                <div className="phone-wrapper-expanded-menu">
                    <img src={phoneIcon} alt="" className="phone-icon" />
                    <p>(832) 885-2369</p>
                </div>
                </a>
                <div id="nav-bar-cta">
                    <Link to="/sell-surplus" className="cta-link">
                        <button>SELL YOUR SURPLUS</button>
                    </Link>
                </div>
            </div>
        </div>

        
    );
}

export default NavBar;

//----------------------------------------------------------------
// Nav Bar Components 
//----------------------------------------------------------------

function NavArrow() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="dropdown-arrow"
        >
            <polyline points="6 9 12 15 18 9" />
        </svg>
    );
}